import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRight, Clock, DollarSign, CheckCircle2, BarChart3, TrendingUp, Shield, Home } from 'lucide-react';
import ExitIntentPopup from '../components/ExitIntentPopup';
import '../styles/QuizLanding.css';

const QuizLanding = () => {
  const navigate = useNavigate();
  const [variant, setVariant] = useState('A'); // A/B Test variant
  const [showExitPopup, setShowExitPopup] = useState(false);

  useEffect(() => {
    // A/B Test: Randomly assign variant or use URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const urlVariant = urlParams.get('variant');
    if (urlVariant === 'B') {
      setVariant('B');
    } else if (!urlVariant) {
      // Random assignment if no parameter
      setVariant(Math.random() > 0.5 ? 'A' : 'B');
    }

    // Exit intent detection
    const alreadyCaptured = localStorage.getItem('exitIntentCaptured');
    if (!alreadyCaptured) {
      const handleMouseLeave = (e) => {
        // Trigger when mouse moves to top of page (intent to leave)
        if (e.clientY <= 5) {
          setShowExitPopup(true);
          // Remove listener after showing once per session
          document.removeEventListener('mouseleave', handleMouseLeave);
        }
      };

      // Add listener after a short delay (don't show immediately)
      const timer = setTimeout(() => {
        document.addEventListener('mouseleave', handleMouseLeave);
      }, 5000); // Wait 5 seconds before enabling

      return () => {
        clearTimeout(timer);
        document.removeEventListener('mouseleave', handleMouseLeave);
      };
    }
  }, []);

  const startQuiz = () => {
    navigate(`/quiz?variant=${variant}`);
  };

  const heroContent = {
    A: {
      hook: "Stuck Running a Business That Runs You?",
      subheading: "Answer 15 questions to discover where your business is leaking value—and how to fix it."
    },
    B: {
      hook: "Ready to Scale Your Business Without the Burnout?",
      subheading: "Answer 15 questions to uncover your growth readiness score and next steps."
    }
  };

  return (
    <div className="quiz-landing-container">
      {/* Header */}
      <header className="quiz-header">
        <div className="quiz-logo-container">
          <img src="/logo.png" alt="LP Global Holdings" className="quiz-logo-img" />
        </div>
        <nav className="quiz-nav">
          <Link to="/" className="nav-home-link">
            <Home size={20} />
            Home
          </Link>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="quiz-hero">
        <div className="quiz-hero-content">
          <div className="section-label">Business Growth Assessment</div>
          <h1 className="quiz-hero-title">{heroContent[variant].hook}</h1>
          <p className="quiz-hero-subtitle">{heroContent[variant].subheading}</p>
          
          {/* Value Proposition */}
          <div className="value-prop-box">
            <h3 className="value-prop-title">Measure & Improve 3 Critical Areas:</h3>
            <div className="value-metrics">
              <div className="metric-item">
                <BarChart3 className="metric-icon" />
                <span>Operational Health</span>
              </div>
              <div className="metric-item">
                <TrendingUp className="metric-icon" />
                <span>Financial Viability</span>
              </div>
              <div className="metric-item">
                <Shield className="metric-icon" />
                <span>Owner Readiness</span>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="cta-container">
            <button onClick={startQuiz} className="btn-primary quiz-cta">
              Start the Assessment
              <ArrowRight size={20} />
            </button>
            <div className="cta-details">
              <div className="cta-detail">
                <Clock size={18} />
                <span>5-7 minutes</span>
              </div>
              <div className="cta-detail">
                <DollarSign size={18} />
                <span>100% Free</span>
              </div>
              <div className="cta-detail">
                <CheckCircle2 size={18} />
                <span>Immediate Results</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Credibility Section */}
      <section className="credibility-section">
        <div className="section-label">Who We Are</div>
        <h2 className="section-title">Meet Mike Lucero</h2>
        <p className="section-subtitle">Builder, Operator, and Turnaround Specialist</p>
        
        {/* Mike's Profile Photo */}
        <div className="profile-photo-container">
          <img src="/mike-photo.png" alt="Mike Lucero" className="profile-photo" />
          <a href="https://instagram.com/michealaponsay" target="_blank" rel="noopener noreferrer" className="instagram-link">
            @michealaponsay
          </a>
        </div>
        
        <div className="credibility-content">
          <div className="bio-section">
            <p className="body-text">
              Mike Lucero is a California-based entrepreneur dedicated to helping burnt-out business owners and entrepreneurs transform their companies into thriving, profitable ventures. With a portfolio spanning strength sports, apparel manufacturing, food concepts, and career-development services, Mike has built a reputation for hands-on leadership, operational discipline, and community-first growth strategies.
            </p>
            
            <h3 className="bio-heading">Current Portfolio:</h3>
            <ul className="portfolio-list">
              <li><strong>Nightmare Muscle</strong> – Strength-sport brand and multi-location gym system</li>
              <li><strong>Fit Print Bros</strong> – Full-service apparel, merch, and large-format print company</li>
              <li><strong>Luncheros LLC</strong> – Pop-up restaurants and food carts in the Bay Area</li>
              <li><strong>LP Global Holdings</strong> – Private equity and turnaround firm focused on under-$2M SMBs</li>
            </ul>

            <p className="body-text emphasis">
              Mike has earned a track record of acquiring businesses with untapped potential, modernizing their operations, scaling revenue, and rebuilding trust in their communities. He's known for his ability to diagnose problems quickly, implement bulletproof systems, and build brands that people talk about.
            </p>

            <p className="body-text">
              A forward-thinking strategist at heart, Mike blends real-world business grit with creative execution, developing playbooks, SOPs, marketing funnels, and automation systems that help small businesses operate like high-performing enterprises.
            </p>
          </div>

          {/* Research Foundation */}
          <div className="research-section">
            <h3 className="research-heading">Our Assessment Framework</h3>
            <p className="body-text">
              This assessment is built on proven business evaluation frameworks, drawing from research published in Harvard Business Review, Stanford GSB studies on operational efficiency, and real-world turnaround data from thousands of small business acquisitions.
            </p>
            <div className="research-sources">
              <div className="source-item">
                <CheckCircle2 size={18} />
                <span>Based on HBR's "The Five Competitive Forces That Shape Strategy"</span>
              </div>
              <div className="source-item">
                <CheckCircle2 size={18} />
                <span>Stanford GSB research on operational leverage and scalability</span>
              </div>
              <div className="source-item">
                <CheckCircle2 size={18} />
                <span>PE industry standards for business valuation and turnaround potential</span>
              </div>
            </div>
          </div>
        </div>

        {/* Final CTA */}
        <div className="final-cta">
          <button onClick={startQuiz} className="btn-primary">
            Start Your Free Assessment
            <ArrowRight size={20} />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="quiz-footer">
        <p className="body-small">© {new Date().getFullYear()} LP Global Holdings. All rights reserved.</p>
      </footer>

      {/* Exit Intent Popup */}
      {showExitPopup && (
        <ExitIntentPopup onClose={() => setShowExitPopup(false)} />
      )}
    </div>
  );
};

export default QuizLanding;
